import { signalStore, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  removeAllEntities,
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { GetMessagesRequest, Message, MessagePaginatedResponse } from '@clientwire/api';
import { inject } from '@angular/core';
import { MessagesWithConversationId, MessageUpdateEvent } from '../interfaces';
import { logger, SharedUtils } from '../utils';
import { WindowService, WireApiService } from '../services';
import { ErrorStore } from './error.store';

export const MessageStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withDevtools('conversation-message-store'),
  withState({ error: null }),
  withEntities<MessagesWithConversationId>(),
  withMethods((store) => {
    const wireApiService = inject(WireApiService);
    const errorStore = inject(ErrorStore);
    const windowService = inject(WindowService);

    return {
      async addMessages(
        conversationId: string,
        newMessages: Message[],
        position: 'prepend' | 'append' = 'prepend',
        overwrite = true,
        secureToAdd = false,
        lastUpdateEvent: MessageUpdateEvent
      ) {
        let oldMessages = [...(store.entityMap()[conversationId]?.messages ?? [])];
        // Filter out messages which are already exists in the store and they just replaceing the old ones,
        // then removed from incoming messages array which I need to append or prepend
        const filteredMessages = this.filterOutExistingMessages(
          conversationId,
          oldMessages,
          newMessages,
          overwrite,
          lastUpdateEvent
        );

        // If no new messages left to append or prepend, return
        if (filteredMessages.length === 0 || !filteredMessages[0]) return;
        if (!filteredMessages[filteredMessages.length - 1]) return;

        if (!secureToAdd && oldMessages.length > 0) {
          const newLatest = filteredMessages[0]?.sequence;
          const newOldest = filteredMessages[filteredMessages.length - 1]?.sequence;
          const oldLatest = oldMessages[0]?.sequence;
          const oldOldest = oldMessages[oldMessages.length - 1]?.sequence;

          const newCompare = position === 'append' ? newLatest : newOldest;
          const oldCompare = position === 'append' ? oldOldest : oldLatest;

          // Early return if any sequence is missing
          if (
            [newLatest, newOldest, oldLatest, oldOldest, newCompare, oldCompare].every(
              (seq) => seq !== null && seq !== undefined
            )
          ) {
            const sequenceDiff = newCompare! - oldCompare!;
            // More than one messages are missing
            if (sequenceDiff > 1) {
              await this.fetchMissingMessages(conversationId, newOldest!, oldLatest!, position);
            } else if (sequenceDiff < -1) {
              await this.fetchMissingMessages(conversationId, oldLatest!, newOldest!, position);
            } else if (sequenceDiff === 0) {
              // This should never happend, but if this happens then let's refetch all the messages from the server
              // what was downloaded by the user
              const oldestMessageSequence = Math.min(oldOldest!, oldLatest!);

              const latestMessageSequence = Math.max(newOldest!, newLatest!);

              await this.fetchMissingMessages(
                conversationId,
                latestMessageSequence,
                oldestMessageSequence,
                position
              );
            } else {
              // EVERYTHING IS FINE
            }

            oldMessages = [...(store.entityMap()[conversationId]?.messages ?? [])];
          }
        }
        // Append or prepend the  original new messages and sorting them
        const messages = (
          position === 'append'
            ? [...oldMessages, ...filteredMessages]
            : [...filteredMessages, ...oldMessages]
        ).sort(SharedUtils.sortBy('sequence'));
        updateState(
          store,
          'addMessages',
          updateEntity({
            id: conversationId,
            changes: {
              messages,
              lastUpdateEvent,
              messageAppendLoading: false,
              messagePrependLoading: false,
            },
          })
        );
      },

      replaceMessage(
        conversationId: string,
        newMessage: Message,
        lastUpdateEvent: MessageUpdateEvent
      ) {
        const oldMessages = [...(store.entityMap()[conversationId]?.messages ?? [])];
        const foundIndex = oldMessages.findIndex(
          (message) => message.sourceId === newMessage.sourceId
        );
        if (foundIndex > -1) {
          const oldMessage = oldMessages[foundIndex];
          let messageToReplace = newMessage;

          // Specific scenario: preserve data: downloadUrl when transitioning from local to server message
          if (
            oldMessage &&
            oldMessage.id.startsWith('new-') &&
            lastUpdateEvent === 'socketMessage' &&
            !newMessage.asset?.downloadUrl &&
            oldMessage.asset?.downloadUrl?.startsWith('data:')
          ) {
            messageToReplace = {
              ...newMessage,
              asset: {
                ...newMessage.asset,
                downloadUrl: oldMessage.asset.downloadUrl,
              },
            };
          }

          oldMessages.splice(foundIndex, 1, messageToReplace);
          updateState(
            store,
            'replaceMessage',
            updateEntity({
              id: conversationId,
              changes: {
                messages: oldMessages.sort(SharedUtils.sortBy('sequence')),
                lastUpdateEvent,
                messageAppendLoading: false,
                messagePrependLoading: false,
              },
            })
          );
        }
      },

      removeMessage(
        conversationId: string,
        messageId?: string,
        lastUpdateEvent: MessageUpdateEvent = 'localMessage'
      ) {
        const oldMessages = [...(store.entityMap()[conversationId]?.messages ?? [])];
        if (!messageId) {
          if (oldMessages.length > 0) {
            oldMessages.shift();
            updateState(
              store,
              'removeMessage',
              updateEntity({
                id: conversationId,
                changes: { messages: oldMessages, lastUpdateEvent },
              })
            );
          } else {
            logger.error('No messages to remove');
          }
        } else {
          const foundIndex = oldMessages.findIndex((message) => message.id === messageId);
          if (foundIndex > -1) {
            oldMessages.splice(foundIndex, 1);
            updateState(
              store,
              'removeMessage',
              updateEntity({
                id: conversationId,
                changes: { messages: oldMessages, lastUpdateEvent },
              })
            );
          } else {
            logger.error('Message not found');
          }
        }
      },

      removeMessages(conversationId: string) {
        updateState(store, 'removeMessages', removeEntity(conversationId));
      },

      clearMessages() {
        updateState(store, 'Clear MessageStore', { error: null });
        updateState(store, 'Clear entities', removeAllEntities());
      },

      async getMessagesOfConversation(
        conversationId: string,
        limit: number,
        lastSequence?: number,
        position: 'prepend' | 'append' = 'prepend',
        secureToAdd = false,
        order: 'ASC' | 'DESC' = 'DESC',
        fetchUntilHasMoreMessages = false
      ): Promise<MessagePaginatedResponse | undefined> {
        errorStore.clearErrorsByLocations([`${conversationId}:getMessagesOfConversation`]);

        const headers: GetMessagesRequest = {
          conversationId,
          limit,
        };

        if (lastSequence) {
          headers.lastSequence = lastSequence;
          headers.order = order;
        }

        try {
          const response = await wireApiService.messagesApi.getMessages(headers);
          if (response.messages?.length > 0) {
            if (order === 'ASC') {
              this.addMessages(
                conversationId,
                response.messages.reverse(),
                position,
                true,
                secureToAdd,
                'serverMessages'
              );
            } else if (order === 'DESC') {
              this.addMessages(
                conversationId,
                response.messages,
                position,
                true,
                secureToAdd,
                'serverMessages'
              );
            } else {
              throw new Error('Invalid order');
            }

            if (fetchUntilHasMoreMessages && response.hasMore) {
              const lastMessageSequence = response.messages[response.messages.length - 1]?.sequence;
              if (lastMessageSequence) {
                this.getMessagesOfConversation(
                  conversationId,
                  limit,
                  lastMessageSequence,
                  position,
                  secureToAdd,
                  order,
                  true
                );
              }
            }
          } else {
            this.setMessageLoading(conversationId, position, false);
          }
          return response;
        } catch (error) {
          this.setMessageLoading(conversationId, position, false);
          logger.error(error);
          try {
            await errorStore.addErrorResponse(
              error,
              'inline',
              true,
              `${conversationId}:getMessagesOfConversation`
            );
            return undefined;
          } catch {
            await errorStore.addErrorResponse(
              { code: 'error.messages.getMessagesOfConversation' },
              'inline',
              false,
              `${conversationId}:getMessagesOfConversation`
            );
            throw error;
          }
        }
      },

      setMessageLoading(conversationId: string, position: 'prepend' | 'append', loading: boolean) {
        if (position === 'append') {
          updateState(
            store,
            'messageAppendLoading',
            updateEntity({
              id: conversationId,
              changes: { messageAppendLoading: loading, lastUpdateEvent: 'loadingChange' },
            })
          );
        } else {
          updateState(
            store,
            'messagePrependLoading',
            updateEntity({
              id: conversationId,
              changes: { messagePrependLoading: loading, lastUpdateEvent: 'loadingChange' },
            })
          );
        }
      },

      recalculateMessagesLimit(conversationId: string, element: HTMLElement) {
        const limit = windowService.calculateLimitSize(element);
        updateState(
          store,
          'recalculateMessagesLimit',
          updateEntity({ id: conversationId, changes: { messagesLimit: limit } })
        );
        return limit;
      },

      async sleep(time: number) {
        return new Promise((resolve) => setTimeout(resolve, time));
      },

      async sendLocalMessageToServer(localMessage: Message) {
        try {
          const conversationId = localMessage.conversationId;
          const serverMessage = await wireApiService.messagesApi.createMessage({
            conversationId: localMessage.conversationId,
            messagePostRequest: {
              kind: localMessage.kind,
              text: localMessage.text,
              location: localMessage.location,
              formattedAddress: localMessage.formattedAddress,
              participantId: localMessage.participantId,
              sourceId: localMessage.sourceId,
            },
          });

          if (serverMessage.asset) {
            serverMessage.asset.downloadUrl = localMessage.asset?.downloadUrl;
          }

          this.replaceMessage(conversationId, serverMessage, 'serverMessages');

          return serverMessage;
        } catch (err) {
          logger.error(err);
          this.addMessageIdToErrors(localMessage, err);
          return null;
        }
      },

      addMessageIdToErrors(localMessage: Message, err: any) {
        const entry = store.entityMap()[localMessage.conversationId];
        if (entry) {
          const errorIds = entry.messageIdsWithError ?? [];
          updateState(
            store,
            'setDownloadUrl',
            updateEntity({
              id: localMessage.conversationId,
              changes: { messageIdsWithError: [...errorIds, localMessage.id] },
            })
          );
        }
      },

      removeMessageIdFromErrors(conversationId: string, messageId: string) {
        const messageIdsWithError = [
          ...(store.entityMap()[conversationId]?.messageIdsWithError ?? []),
        ];
        messageIdsWithError.splice(messageIdsWithError.indexOf(messageId), 1);
        updateState(
          store,
          'remove messageId from messageIdsWithError',
          updateEntity({ id: conversationId, changes: { messageIdsWithError } })
        );
      },

      setDownloadUrl(downloadUrl: string, messageId: string, conversationId: string) {
        const messages = [...(store.entityMap()[conversationId]?.messages ?? [])];
        const foundIndex = messages.findIndex((message) => message.id === messageId);
        if (foundIndex > -1 && messages[foundIndex]?.asset) {
          messages[foundIndex].asset.downloadUrl = downloadUrl;
          updateState(
            store,
            'setDownloadUrl',
            updateEntity({ id: conversationId, changes: { messages } })
          );
        }
      },

      filterOutExistingMessages(
        conversationId: string,
        oldMessages: Message[],
        newMessages: Message[],
        overwrite: boolean,
        lastUpdateEvent: MessageUpdateEvent = 'localMessage'
      ) {
        return newMessages.filter((newMessage) => {
          const indexOfExistingMessage = oldMessages.findIndex(
            (message) => message.id === newMessage.id || message.sourceId === newMessage.sourceId
          );
          if (indexOfExistingMessage > -1) {
            if (overwrite) {
              this.replaceMessage(conversationId, newMessage, lastUpdateEvent);
            }
            return false;
          }
          return true;
        });
      },

      async fetchMissingMessages(
        conversationId: string,
        toSequence: number,
        fromSequence: number,
        position: 'prepend' | 'append'
      ) {
        const totalMissing = Math.abs(toSequence - fromSequence) - 1;

        if (totalMissing <= 0) {
          return; // No messages to fetch
        }

        let remainingToFetch = totalMissing;
        let currentFromSequence = fromSequence;

        logger.warn(
          `Fetching ${totalMissing} missing messages between ${fromSequence} and ${toSequence}`
        );

        while (remainingToFetch > 0) {
          const limit = Math.min(remainingToFetch, 100);

          logger.debug(`Fetching batch: ${limit} messages, remaining: ${remainingToFetch}`);

          await this.getMessagesOfConversation(
            conversationId,
            limit,
            currentFromSequence,
            position,
            true,
            position === 'append' ? 'DESC' : 'ASC'
          );

          // Update counters for next iteration
          remainingToFetch -= limit;

          // Update currentFromSequence based on the order/direction
          if (position === 'append') {
            // For append (DESC order), we're going backwards in sequence numbers
            currentFromSequence = currentFromSequence - limit;
          } else {
            // For prepend (ASC order), we're going forwards in sequence numbers
            currentFromSequence = currentFromSequence + limit;
          }
        }

        logger.debug('Finished fetching all missing messages');
      },
    };
  })
);
